import { BaseChartAPIProps } from '@chart/types/create.types';
import {
  DOMAIN_CHANGE_CUSTOM_EVENT,
  DomainChangeEvent,
  GUTTER_SIZE_CUSTOM_EVENT,
  GutterSizeEvent,
  HOVER_CUSTOM_EVENT,
  HoverEvent,
  INSIGHTS_IN_DOMAIN_CHANGE_CUSTOM_EVENT,
  InsightsInDomainEvent,
  PRICE_HOVER_CUSTOM_EVENT,
  PriceHover,
  PriceHoverEvent,
  RIGHT_CLICK_EVENT,
  RightClickEvent,
} from '@chart/types/events.types';
import { InsightsInDomain } from '@chart/types/timeseries.types';
import { isWithinYAxis } from '@chart/utils/axis/axis-utils';
import { TURBO_CHART_INNER_PADDING } from '@chart/utils/constants';
import { showZoomInToViewText } from '@chart/utils/dates';
import { getInsightsInDomain } from '@chart/utils/insights/insights';
import { getActivePanes, getPaneDetails } from '@chart/utils/pane/pane-utils';
import { compareObjectKeys } from '@helpers';

export const dispatchDomainChangeEvent = (
  base: BaseChartAPIProps,
  isSameDomain: boolean
) => {
  const roundedDomain = [Math.ceil(base.domain[0]), Math.floor(base.domain[1])];
  base.canvasElement.dispatchEvent(
    new CustomEvent(DOMAIN_CHANGE_CUSTOM_EVENT, {
      detail: {
        domain: [base.domain[0], base.domain[1]],
        roundedDomain,
        isSameDomain:
          !roundedDomain.some((domain, idx) => domain !== base.domain[idx]) ||
          isSameDomain,
      },
    }) as DomainChangeEvent
  );
};

export const dispatchInsightsInDomainEvent = (base: BaseChartAPIProps) => {
  const { domain, x, options, primaryAsset, panes } = base;
  const showHelperText = showZoomInToViewText(primaryAsset.ts, domain);
  const helperText = showHelperText
    ? {
        right: options.width - options.gutters.y - x.xScale.range()[1] / 2,
        y: options.height - options.gutters.x - TURBO_CHART_INNER_PADDING,
      }
    : null;
  const insightsInDomain = showHelperText
    ? []
    : getActivePanes(panes).reduce((insights, chartPane) => {
        if (chartPane.y.length) {
          insights.push(
            ...getInsightsInDomain({
              primaryAsset,
              domain,
              x,
              y: chartPane.y,
              insights: chartPane.insights,
              width: base.options.width,
            })
          );
        }

        return insights;
      }, [] as InsightsInDomain[]);
  base.canvasElement.dispatchEvent(
    new CustomEvent(INSIGHTS_IN_DOMAIN_CHANGE_CUSTOM_EVENT, {
      detail: { insightsInDomain, helperText },
    }) as InsightsInDomainEvent
  );
};

interface DispatchPriceHoverEventProps {
  base: BaseChartAPIProps;
  priceHover?: PriceHover;
}

export const dispatchPriceHoverEvent = ({
  base,
  priceHover,
}: DispatchPriceHoverEventProps) => {
  const event = new CustomEvent(PRICE_HOVER_CUSTOM_EVENT, {
    detail: priceHover,
  }) as PriceHoverEvent;
  base.canvasElement.dispatchEvent(event);
  document.dispatchEvent(event);
};

export const dispatchGutterSizeEvent = (
  base: BaseChartAPIProps,
  previousBase?: BaseChartAPIProps
) => {
  if (
    previousBase &&
    compareObjectKeys(base, previousBase, [
      'options.gutters.x',
      'options.gutters.y',
      'panes',
    ])
  ) {
    return;
  }

  const { panes, options } = base;
  const paneLimitExceeded = panes.length >= options.config.paneMaxCount;
  base.canvasElement.dispatchEvent(
    new CustomEvent(GUTTER_SIZE_CUSTOM_EVENT, {
      detail: {
        x: base.options.gutters.x,
        y: base.options.gutters.y,
        paneLimitExceeded,
        panes,
        yAxisSizes: base.yAxisSizes,
      },
    }) as GutterSizeEvent
  );
};

export const dispatchRightClickEvent = ({
  base,
  mouseX,
  mouseY,
  isWithinYAxis,
}: {
  base: BaseChartAPIProps;
  mouseX: number;
  mouseY: number;
  isWithinYAxis: boolean;
}) => {
  base.canvasElement.dispatchEvent(
    new CustomEvent<RightClickEvent>(RIGHT_CLICK_EVENT, {
      detail: {
        mouseX,
        mouseY,
        isWithinYAxis,
      },
    })
  );
};

export const dispatchHoverEvent = (
  event: MouseEvent,
  base: BaseChartAPIProps
) => {
  const detail: HoverEvent = {
    isWithinYAxis: isWithinYAxis(event, base.options),
    paneDetails: getPaneDetails(event, base),
  };

  base.canvasElement.dispatchEvent(
    new CustomEvent<HoverEvent>(HOVER_CUSTOM_EVENT, {
      detail,
    })
  );
};
